import './applianceDetails.scss';

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LocalizedLink } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import AvailabilityComponent from '../../components/Primitives/AvailabilityComponent';
import { IAppliance } from '../../interfaces';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import empty_star from '../../images/englander-icons/Star-Empty-Icon.svg';
import full_star from '../../images/englander-icons/Star-Full-Icon.svg';
import rightArrowIcon from '../../images/englander-icons/Right-Arrow-Icon.svg';
import rightArrowIconBrown from '../../images/englander-icons/Right-Arrow-Icon-Brown.svg';
import { scrollToRef } from '../../utils';
import starHalfIcon from '../../images/englander-icons/Star-Half-Icon.svg';
import thickIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Thick-Icon.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import ApplianceDetailsMadeIn from './ApplianceDetailsMadeIn';
import PriceNotice from './PriceNotice';

export default function ApplianceDetails(props: {
    appliance: IAppliance;
    reviewsRef: React.MutableRefObject<any>;
    infoRef: React.MutableRefObject<any>;
    specialPriceEnd?: string;
}) {
    const [qtyValue, setQtyValue] = useState(1);
    const { appliance, reviewsRef } = props;
    const { currentLocale } = usePageLocalization();
    const [price] = useProductPrice(appliance.code as string);
    const { t } = useTranslation('layout');

    console.log(appliance);

    const productReviews = useProductReviews(appliance.code);

    const promoText =
        appliance?.attributes?.TextPrix?.values[0]?.[currentLocale];

    const RenderStars = useCallback(() => {
        const averageRating = productReviews?.average ?? 0;
        const stars = [];
        let hasRating = false;

        for (let i = 1; i < 6; i++) {
            if (i <= averageRating) {
                hasRating = true;
                stars.push(
                    <img width={16} key={`star_${i}`} src={full_star} alt='' />
                );
            } else {
                const currentFloor = Math.floor(averageRating);
                const projectedFloor = Math.floor(averageRating - 0.5);
                if (projectedFloor === currentFloor && i === currentFloor + 1) {
                    stars.push(
                        <img
                            width={16}
                            key={`star_${i}`}
                            src={starHalfIcon}
                            alt=''
                        />
                    );
                } else {
                    stars.push(
                        <img
                            width={16}
                            key={`star_${i}`}
                            src={empty_star}
                            alt=''
                        />
                    );
                }
            }
        }

        if (hasRating) return <div>{stars.map((star) => star)}</div>;
        else return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productReviews?.reviews, productReviews]);

    const showApplianceForFreeDelivery = () => {
        const category = appliance.category.toLowerCase();
        if (
            category.includes('outdoor fireplace') ||
            category.includes('camp stoves')
        ) {
            return false;
        }

        return true;
    };

    return (
        <div className='appliance-details-container'>
            <span className='appliance-details-code'>{appliance.code}</span>
            <h1 className='appliance-details-title'>
                {appliance?.title?.[currentLocale]}
            </h1>
            {productReviews && productReviews?.count !== 0 ? (
                <div className='rating-section'>
                    <div
                        className='rating'
                        onClick={() => {
                            scrollToRef(reviewsRef, 140);
                        }}
                    >
                        <RenderStars />{' '}
                        <span className='rating-count'>
                            {`(${productReviews.count})`}
                        </span>
                        <span className='see-ratings'>
                            {t('appliance.see-reviews')}
                            <img src={rightArrowIcon} alt='' />
                        </span>
                    </div>
                </div>
            ) : (
                <div className='rating-section'>
                    <div
                        className='rating'
                        onClick={() => {
                            scrollToRef(reviewsRef, 140);
                        }}
                    >
                        <span className='see-ratings' style={{ marginLeft: 0 }}>
                            {t('appliance.review-product')}
                        </span>
                        <img
                            className='see-ratings-icon'
                            src={rightArrowIconBrown}
                            alt=''
                        />
                    </div>
                </div>
            )}
            <div className='price-section'>
                {getProductPrice()}
                {promoText && <span className='promo-text'>{promoText}</span>}
            </div>
            <PriceNotice price={price} />
            <AvailabilityComponent product={appliance} />

            <div className='add-to-cart-section'>
                <AddToCartButtonContainer
                    product={appliance}
                    qtyValue={qtyValue}
                    setQtyValue={setQtyValue}
                />
            </div>

            {showApplianceForFreeDelivery() && (
                <LocalizedLink
                    className='curbside-delivery'
                    to='/shipping-policy/'
                >
                    {t('appliance.curbside-delivery')}
                </LocalizedLink>
            )}

            <div className='appliance-details-spect'>
                {(price?.madeIn?.isMadeInCanada ||
                    price?.madeIn?.isMadeInUSA) && (
                   <>
                        <span className='details-spect-name made-in'>
                            {t('appliance.made-in')}
                        </span>
                        <span className='details-spect made-in'>
                            <ApplianceDetailsMadeIn price={price} />
                        </span>
                    </>
                )}
                {renderSpecs(appliance.category.split('/')[3])}
                <div className='need-assistance'>
                    <span className='question-mark'>{'?'}</span>
                    <span>
                        {t('appliance.need-assistance')}
                        <LocalizedLink className='nav-contact' to='/contact/'>
                            {t('appliance.contact-us')}
                        </LocalizedLink>
                    </span>
                </div>
                <div className={`find-replacement`}>
                    <LocalizedLink
                        to={`/archives/${appliance.code.toLowerCase()}`}
                        className='find-replacement-btn'
                    >
                        {t('appliance.find-replacements')}
                        <img className={`iconRight`} src={rightArrowIcon} />
                    </LocalizedLink>
                </div>
            </div>
        </div>
    );

    function getProductPrice() {
        if (price?.regularPrice.value === 0) {
            return '--';
        }
        if (price?.regularPrice.value === price?.finalPrice.value) {
            return (
                <span className='normal-price'>
                    {LocalePrice(currentLocale, price?.regularPrice.value)}
                </span>
            );
        } else if (
            price?.regularPrice.value &&
            price?.finalPrice.value &&
            price.regularPrice.value !== price.finalPrice.value
        ) {
            return (
                <>
                    <span className='regular-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                    <span className='final-price'>
                        {LocalePrice(currentLocale, price?.finalPrice.value)}
                    </span>
                </>
            );
        }
    }

    function renderSpecs(type: string) {
        let attributes: string[] = [];

        switch (type) {
            case 'Wood Stoves':
                attributes = ['Surfi', 'size', 'LongBuci', 'Emiss'];
                break;

            case 'Pellet Stoves':
                attributes = ['Surfi', 'size', 'TempComb'];
                break;

            case 'Wood Furnaces':
                attributes = ['Surfi', 'LongBuci', 'TempComb'];
                break;

            case 'Camp Stoves':
                attributes = ['VolComi', 'LongBuci', 'Poidi'];
                break;

            default:
                break;
        }

        return attributes.map((attr, index) => {
            return (
                <>
                    <span className='details-spect-name'>
                        {appliance.attributes?.[attr]?.name?.[currentLocale]}
                    </span>
                    <div className='details-spect' key={index}>
                        <img src={thickIcon} alt='' />
                        <span>
                            {
                                appliance.attributes?.[attr]?.values?.[0]?.[
                                    currentLocale
                                ]
                            }
                        </span>
                    </div>
                </>
            );
        });
    }
}
